/**
 * Admin V3 — Corporate client edit modal (AV3 A-6)
 *
 * Operator edits a corporate account via PATCH /enterprises/{id}
 * (RBAC: enterprise:update). Reuses the canonical endpoint verbatim; the backend
 * filters to its allowed fields and owns the pricing-contract sync when base_rate
 * changes. Status (activate/suspend) is handled separately by the row toggle.
 * No new business logic, no backend change.
 */
import React, { useState } from 'react';
import { Loader2, X, Building2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

const Field = ({ label, k, value, onChange, type = 'text' }) => (
  <div>
    <label className="text-[12px] font-semibold text-slate-600">{label}</label>
    <input type={type} value={value} onChange={onChange} className={inputCls} data-testid={`adminv3-corporate-edit-${k}`} />
  </div>
);

const s = (v) => (v == null ? '' : String(v));

const CorporateEditModal = ({ open, enterprise, onClose, onDone, locale = DEFAULT_LOCALE }) => {
  const enterpriseId = enterprise?.id;
  const m = useAdminMutation(`/enterprises/${enterpriseId || '_'}`, 'patch');
  const { showSuccess, showError } = useToast();

  const [f, setF] = useState({
    name: s(enterprise?.name), phone: s(enterprise?.phone), address: s(enterprise?.address),
    contact_name: s(enterprise?.contact_name), billing_email: s(enterprise?.billing_email),
    billing_address: s(enterprise?.billing_address), payment_terms: s(enterprise?.payment_terms),
    max_monthly_budget: s(enterprise?.max_monthly_budget), base_rate: s(enterprise?.base_rate),
    siret: s(enterprise?.siret), negotiated_discount: s(enterprise?.negotiated_discount),
  });
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  if (!open || !enterprise) return null;

  const valid = f.name.trim() && f.address.trim() && f.contact_name.trim();

  const submit = () => {
    m.mutate(
      {
        name: f.name.trim(),
        phone: f.phone.trim() || null,
        address: f.address.trim(),
        contact_name: f.contact_name.trim(),
        billing_email: f.billing_email.trim() || null,
        billing_address: f.billing_address.trim() || null,
        payment_terms: f.payment_terms === '' ? null : Number(f.payment_terms),
        max_monthly_budget: numOrNull(f.max_monthly_budget),
        base_rate: numOrNull(f.base_rate),
        siret: f.siret.trim() || null,
        negotiated_discount: numOrNull(f.negotiated_discount),
      },
      {
        onSuccess: () => {
          showSuccess(t('corporate.edit.ok', locale));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('corporate.edit.error', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-corporate-edit-modal">
      <div className="w-[600px] max-w-[94vw] max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-[17px] font-semibold text-slate-900">
            <Building2 className="h-4 w-4 text-indigo-500" />
            {t('corporate.edit.title', locale).replace('{name}', enterprise.name || '')}
          </h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-corporate-edit-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label={t('corporate.field.name', locale)}        k="name"         value={f.name}         onChange={set('name')} />
          <Field label={t('corporate.field.contactName', locale)} k="contact_name" value={f.contact_name} onChange={set('contact_name')} />
          <Field label={t('corporate.field.phone', locale)}       k="phone"        value={f.phone}        onChange={set('phone')} />
          <Field label={t('corporate.field.siret', locale)}       k="siret"        value={f.siret}        onChange={set('siret')} />
          <div className="md:col-span-2">
            <Field label={t('corporate.field.address', locale)}   k="address"      value={f.address}      onChange={set('address')} />
          </div>
          <Field label={t('corporate.field.billingEmail', locale)}   k="billing_email"   value={f.billing_email}   onChange={set('billing_email')} type="email" />
          <Field label={t('corporate.field.billingAddress', locale)} k="billing_address" value={f.billing_address} onChange={set('billing_address')} />
          <Field label={t('corporate.field.paymentTerms', locale)}   k="payment_terms"   value={f.payment_terms}   onChange={set('payment_terms')} type="number" />
          <Field label={t('corporate.field.maxBudget', locale)}      k="max_monthly_budget" value={f.max_monthly_budget} onChange={set('max_monthly_budget')} type="number" />
          <Field label={t('corporate.field.baseRate', locale)}       k="base_rate"       value={f.base_rate}       onChange={set('base_rate')} type="number" />
          <Field label={t('corporate.field.discount', locale)}       k="negotiated_discount" value={f.negotiated_discount} onChange={set('negotiated_discount')} type="number" />
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-corporate-edit-missing">
            {t('corporate.edit.required', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-corporate-edit-dismiss">
            {t('corporate.edit.cancel', locale)}
          </Button>
          <Button
            variant="default" size="sm" onClick={submit} disabled={!valid || m.loading}
            data-testid="adminv3-corporate-edit-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t('corporate.edit.confirm', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CorporateEditModal;
